/**
 * Teacher marketplace: pack tabs + Skylite OS vinyl shelf (playground downloads — stubs only).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import {
  TRAINING_PACKS,
  SKYLITE_VINYL_BOXES,
  SKYLITE_BOX_PRICE_TIERS,
  SKYLITE_EXAM_PREP_CHART,
  getSkyliteTier
} from '../lib/trainingPackCatalog.js';

function statusLabel(status, owned) {
  if (owned) return 'Included';
  if (status === 'available') return 'Available to purchase';
  return 'Coming soon';
}

function VinylCard({ box, flipped, onFlip }) {
  const tier = getSkyliteTier(box.tier);
  return (
    <Box
      sx={{
        width: { xs: 128, sm: 156 },
        perspective: 900,
        transition: 'transform 0.2s ease',
        '&:hover': { transform: flipped ? 'none' : 'translateY(-6px)' }
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '1 / 1',
          transformStyle: 'preserve-3d',
          transition: 'transform 0.45s ease',
          transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
        }}
      >
        {/* Front — jacket */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden'
          }}
        >
          <Box
            component="img"
            src={box.jacketSrc}
            alt=""
            loading="eager"
            decoding="async"
            className="notranslate"
            translate="no"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              objectPosition: 'center',
              borderRadius: '4px',
              boxShadow: '0 10px 28px rgba(0,0,0,0.55), 0 0 0 2px rgba(15,23,42,0.9)',
              display: 'block',
              bgcolor: '#0f172a'
            }}
          />
          <Tooltip title="Flip for details">
            <IconButton
              type="button"
              size="small"
              aria-label={`Flip ${box.title} for details`}
              onClick={() => onFlip(box.id)}
              sx={{
                position: 'absolute',
                top: 6,
                right: 6,
                bgcolor: 'rgba(2,6,23,0.82)',
                color: '#e2e8f0',
                border: '1px solid rgba(167,139,250,0.45)',
                '&:hover': { bgcolor: 'rgba(30,27,75,0.95)', color: '#fff' }
              }}
            >
              <SyncAltIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Back — short blurb */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            borderRadius: '4px',
            p: 1.25,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'linear-gradient(165deg, #1e1b4b 0%, #0f172a 100%)',
            background: 'linear-gradient(165deg, #312e81 0%, #0f172a 70%)',
            border: '1px solid rgba(167,139,250,0.5)',
            boxShadow: '0 10px 28px rgba(0,0,0,0.55)'
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.5 }}>
            <Typography
              variant="caption"
              className="notranslate"
              translate="no"
              sx={{ fontWeight: 900, color: '#f8fafc', lineHeight: 1.2, pr: 0.5 }}
            >
              {box.title}
            </Typography>
            <IconButton
              type="button"
              size="small"
              aria-label={`Close ${box.title} details`}
              onClick={() => onFlip(null)}
              sx={{ color: '#cbd5e1', p: 0.25 }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Stack>
          <Chip
            size="small"
            label={tier.label}
            sx={{
              alignSelf: 'flex-start',
              mb: 0.75,
              height: 20,
              fontSize: 10,
              fontWeight: 800,
              bgcolor: 'rgba(129,140,248,0.25)',
              color: '#e0e7ff'
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(248,250,252,0.88)',
              lineHeight: 1.35,
              flex: 1,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 5,
              WebkitBoxOrient: 'vertical'
            }}
          >
            {box.summary}
          </Typography>
          <Button
            type="button"
            size="small"
            onClick={() => onFlip(box.id, { openPanel: true })}
            sx={{
              mt: 0.75,
              textTransform: 'none',
              fontWeight: 800,
              fontSize: 11,
              color: '#c4b5fd',
              minWidth: 0,
              p: 0,
              justifyContent: 'flex-start',
              '&:hover': { bgcolor: 'transparent', color: '#fff' }
            }}
          >
            Full details →
          </Button>
        </Box>
      </Box>
      <Typography
        variant="caption"
        className="notranslate"
        translate="no"
        sx={{
          display: 'block',
          textAlign: 'center',
          mt: 1,
          fontWeight: 800,
          color: flipped ? '#c4b5fd' : '#e2e8f0',
          letterSpacing: '0.02em'
        }}
      >
        {box.title}
      </Typography>
    </Box>
  );
}

function SkyliteDetailPanel({ box, onClose, panelRef }) {
  if (!box) return null;
  const tier = getSkyliteTier(box.tier);
  return (
    <Paper
      ref={panelRef}
      elevation={0}
      sx={{
        mt: 2.5,
        p: { xs: 2, sm: 2.5 },
        borderRadius: 3,
        position: 'relative',
        background: 'linear-gradient(165deg, #1e1b4b 0%, #0f172a 55%, #020617 100%)',
        border: '1px solid rgba(167, 139, 250, 0.45)',
        scrollMarginTop: 88
      }}
    >
      <IconButton
        type="button"
        aria-label="Close box details"
        onClick={onClose}
        sx={{ position: 'absolute', top: 8, right: 8, color: '#94a3b8' }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
        <Box
          component="img"
          src={box.jacketSrc}
          alt=""
          className="notranslate"
          translate="no"
          sx={{
            width: { xs: 96, sm: 112 },
            height: { xs: 96, sm: 112 },
            objectFit: 'contain',
            borderRadius: 1,
            bgcolor: '#020617',
            flexShrink: 0,
            boxShadow: '0 8px 20px rgba(0,0,0,0.45)'
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0, pr: { sm: 5 } }}>
          <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h6" className="notranslate" translate="no" sx={{ fontWeight: 900, m: 0 }}>
              {box.title}
            </Typography>
            <Chip size="small" label={box.difficulty} sx={{ fontWeight: 800, bgcolor: 'rgba(51,65,85,0.8)', color: '#e2e8f0' }} />
            <Chip
              size="small"
              label={tier.label}
              sx={{ fontWeight: 800, bgcolor: 'rgba(129,140,248,0.22)', color: '#e0e7ff' }}
            />
            <Chip size="small" label="Coming soon" sx={{ fontWeight: 800, bgcolor: 'rgba(245,158,11,0.2)', color: '#fde68a' }} />
          </Stack>
          <Typography variant="body2" sx={{ color: 'rgba(248,250,252,0.92)', lineHeight: 1.55, mb: 1.25, maxWidth: 720 }}>
            {box.summary}
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 1.5 }}>
            {(box.skills || []).map((s) => (
              <Chip
                key={s}
                size="small"
                label={s}
                sx={{ fontWeight: 700, bgcolor: 'rgba(15,23,42,0.85)', color: '#cbd5e1', border: '1px solid rgba(100,116,139,0.5)' }}
              />
            ))}
          </Stack>
          <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', fontWeight: 700, mb: 0.5 }}>
            Planned pricing (illustrative — not live)
          </Typography>
          <Typography variant="body2" sx={{ color: '#e2e8f0', fontWeight: 800, mb: 0.75 }}>
            12‑month ${tier.yearUsd} · 48‑hour rental ${tier.rental48hUsd}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: '#64748b', maxWidth: 640, lineHeight: 1.45 }}>
            Downloads will require Google SSO (entitled account). File links will be short-lived so boxes are not casually
            shared. Graduates can transfer a remaining 12‑month license to their personal Google account — rentals stay
            non-transferable. Runs on VirtualBox or VMware (not Chromebook).
          </Typography>
        </Box>
      </Stack>

      <Tooltip title="Downloads not enabled yet">
        <span>
          <IconButton
            type="button"
            disabled
            aria-label="Download coming soon"
            sx={{
              position: 'absolute',
              right: 16,
              bottom: 16,
              bgcolor: 'rgba(129,140,248,0.2)',
              color: '#c4b5fd',
              border: '1px solid rgba(167,139,250,0.45)',
              '&.Mui-disabled': { color: '#64748b', bgcolor: 'rgba(30,41,59,0.8)' }
            }}
          >
            <DownloadIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Paper>
  );
}

export default function TeacherPackMarketplace({
  enabledPackIds = [],
  institutionName = '',
  backLabel = 'Back to dashboard',
  onBack
}) {
  const owned = useMemo(() => new Set(enabledPackIds || []), [enabledPackIds]);
  const [tab, setTab] = useState(0);
  const [flippedId, setFlippedId] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const detailPanelRef = useRef(null);
  const pack = TRAINING_PACKS[tab] || TRAINING_PACKS[0];
  const isOwned = owned.has(pack.id);
  const detailBox = useMemo(
    () => SKYLITE_VINYL_BOXES.find((b) => b.id === detailId) || null,
    [detailId]
  );

  useEffect(() => {
    if (!detailId) return;
    const t = window.setTimeout(() => {
      detailPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
    return () => window.clearTimeout(t);
  }, [detailId]);

  function handleBack(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (typeof onBack === 'function') onBack();
  }

  function handleFlip(id, opts = {}) {
    if (!id) {
      setFlippedId(null);
      return;
    }
    setFlippedId((prev) => (prev === id && !opts.openPanel ? null : id));
    if (opts.openPanel) setDetailId(id);
  }

  return (
    <Box
      sx={{
        minHeight: '100%',
        color: '#f8fafc',
        background:
          'radial-gradient(ellipse 100% 70% at 20% 0%, rgba(15, 23, 42, 0.95) 0%, #020617 50%, #000 100%)'
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 40,
          px: { xs: 2, sm: 3 },
          py: 1.5,
          borderBottom: '1px solid rgba(148, 163, 184, 0.25)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'rgba(2, 6, 23, 0.96)',
          backdropFilter: 'blur(8px)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Button
            type="button"
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              color: '#e2e8f0',
              borderColor: 'rgba(148, 163, 184, 0.45)',
              flexShrink: 0,
              '&:hover': {
                borderColor: 'rgba(186, 230, 253, 0.8)',
                bgcolor: 'rgba(30, 58, 138, 0.35)'
              }
            }}
          >
            {backLabel}
          </Button>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="overline" sx={{ letterSpacing: '0.14em', color: '#94a3b8', fontWeight: 800 }}>
              CommandLine Marketplace
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
              Level packs{institutionName ? ` · ${institutionName}` : ''}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, sm: 3 }, py: 2.5 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            mb: 2,
            minHeight: 48,
            // Active tab used an outer ring that MUI's scroller clips (esp. last tab / GRC).
            '& .MuiTabs-indicator': { display: 'none' },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 800,
              minHeight: 40,
              mr: 1,
              borderRadius: 2,
              color: '#94a3b8',
              border: '1px solid transparent',
              boxSizing: 'border-box'
            }
          }}
        >
          {TRAINING_PACKS.map((p, i) => (
            <Tab
              key={p.id}
              label={p.tabLabel}
              sx={{
                bgcolor: tab === i ? p.accent.chip : 'rgba(15,23,42,0.7)',
                color: tab === i ? '#fff !important' : undefined,
                borderColor: tab === i ? p.accent.border : 'rgba(51,65,85,0.8) !important',
                // Inset ring stays inside the tab — no top/side clip when selected
                boxShadow: tab === i ? `inset 0 0 0 1px ${p.accent.tab}` : 'none'
              }}
            />
          ))}
        </Tabs>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 3,
            background: pack.accent.panel,
            border: `1px solid ${pack.accent.border}`,
            mb: 4
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Typography variant="h5" sx={{ fontWeight: 900 }}>
              {pack.title}
            </Typography>
            <Chip
              label={statusLabel(pack.status, isOwned)}
              sx={{
                fontWeight: 800,
                bgcolor: isOwned ? 'rgba(34,197,94,0.25)' : pack.accent.chip,
                color: '#fff',
                border: `1px solid ${isOwned ? 'rgba(74,222,128,0.5)' : pack.accent.border}`
              }}
            />
          </Stack>
          <Typography variant="body1" sx={{ color: 'rgba(248,250,252,0.92)', mb: 1, maxWidth: 720, lineHeight: 1.55 }}>
            {pack.summary}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(226,232,240,0.7)', fontWeight: 700, display: 'block', mb: 2 }}>
            {pack.levelRangeHint}
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1, letterSpacing: '0.04em' }}>
            Curriculum
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 2.5 }}>
            {pack.curriculum.map((line) => (
              <Typography component="li" key={line} variant="body2" sx={{ color: 'rgba(248,250,252,0.88)', mb: 0.6, lineHeight: 1.45 }}>
                {line}
              </Typography>
            ))}
          </Box>
          {isOwned ? (
            <Typography variant="body2" sx={{ color: '#86efac', fontWeight: 700 }}>
              This pack is active for your school — it appears on the Learning Center desktop after Cybersecurity 101.
            </Typography>
          ) : pack.status === 'available' ? (
            <Button
              variant="contained"
              disabled
              sx={{
                textTransform: 'none',
                fontWeight: 900,
                bgcolor: pack.accent.tab,
                color: '#0f172a',
                '&.Mui-disabled': { bgcolor: pack.accent.tab, color: '#0f172a', opacity: 0.85 }
              }}
            >
              Purchase coming soon — contact CommandLine sales
            </Button>
          ) : (
            <Typography variant="body2" sx={{ color: 'rgba(248,250,252,0.75)', fontWeight: 600 }}>
              Curriculum preview only — levels unlock when this pack ships.
            </Typography>
          )}
        </Paper>

        {/* Skylite practice playground */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="overline" sx={{ letterSpacing: '0.16em', color: '#a78bfa', fontWeight: 900 }}>
            Skylite OS · Practice playground
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.5 }}>
            Downloadable CTF boxes
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2, maxWidth: 720, lineHeight: 1.55 }}>
            Original system-exploitation playgrounds for VirtualBox or VMware — red team reps and OSCP-style prep.
            Not for Learning Center points. No purchase gate: grab any mix. Flip a jacket for details; downloads and
            Google SSO licensing come later (12‑month or 48‑hour rental).
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 2.5 },
            mb: 2.5,
            borderRadius: 3,
            bgcolor: 'rgba(15,23,42,0.85)',
            border: '1px solid rgba(71,85,105,0.6)'
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1.25, letterSpacing: '0.04em' }}>
            Price tiers (illustrative)
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            {Object.values(SKYLITE_BOX_PRICE_TIERS).map((t) => (
              <Box
                key={t.id}
                sx={{
                  flex: 1,
                  p: 1.5,
                  borderRadius: 2,
                  border: '1px solid rgba(100,116,139,0.45)',
                  bgcolor: 'rgba(2,6,23,0.55)'
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 900, color: '#e0e7ff', mb: 0.35 }}>
                  {t.label}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', mb: 0.75, lineHeight: 1.4 }}>
                  {t.blurb}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 800, color: '#cbd5e1' }}>
                  ${t.yearUsd}/yr · ${t.rental48hUsd} / 48h
                </Typography>
              </Box>
            ))}
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 2.5 },
            mb: 2.5,
            borderRadius: 3,
            background: 'linear-gradient(165deg, #0f172a 0%, #1e1b4b 100%)',
            border: '1px solid rgba(167,139,250,0.35)'
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.5 }}>
            {SKYLITE_EXAM_PREP_CHART.title}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', mb: 1.5, lineHeight: 1.45, maxWidth: 720 }}>
            {SKYLITE_EXAM_PREP_CHART.disclaimer}
          </Typography>
          <Box
            component="table"
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              mb: 1.5,
              '& th, & td': {
                textAlign: 'left',
                py: 1,
                px: 1,
                borderBottom: '1px solid rgba(71,85,105,0.55)',
                verticalAlign: 'top'
              },
              '& th': { color: '#a78bfa', fontWeight: 900, fontSize: 12, letterSpacing: '0.04em' }
            }}
          >
            <thead>
              <tr>
                <th>Track</th>
                <th>Skill focus</th>
                <th>Suggested boxes</th>
              </tr>
            </thead>
            <tbody>
              {SKYLITE_EXAM_PREP_CHART.tracks.map((track) => (
                <tr key={track.id}>
                  <td>
                    <Typography variant="body2" sx={{ fontWeight: 800, color: '#e2e8f0' }}>
                      {track.label}
                    </Typography>
                  </td>
                  <td>
                    <Typography variant="caption" sx={{ color: '#94a3b8', lineHeight: 1.4 }}>
                      {track.skillFocus}
                    </Typography>
                  </td>
                  <td>
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {track.boxIds.map((id) => {
                        const b = SKYLITE_VINYL_BOXES.find((x) => x.id === id);
                        return (
                          <Chip
                            key={id}
                            size="small"
                            className="notranslate"
                            translate="no"
                            label={b?.title || id}
                            onClick={() => {
                              setFlippedId(id);
                              setDetailId(id);
                            }}
                            sx={{
                              fontWeight: 800,
                              cursor: 'pointer',
                              bgcolor: 'rgba(129,140,248,0.18)',
                              color: '#e0e7ff',
                              '&:hover': { bgcolor: 'rgba(129,140,248,0.35)' }
                            }}
                          />
                        );
                      })}
                    </Stack>
                  </td>
                </tr>
              ))}
            </tbody>
          </Box>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: '1px dashed rgba(167,139,250,0.45)',
              bgcolor: 'rgba(2,6,23,0.45)'
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 900, color: '#e0e7ff', mb: 0.35 }}>
              {SKYLITE_EXAM_PREP_CHART.bundle.title}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', mb: 0.5, lineHeight: 1.4 }}>
              Includes{' '}
              {SKYLITE_EXAM_PREP_CHART.bundle.includesBoxIds
                .map((id) => SKYLITE_VINYL_BOXES.find((b) => b.id === id)?.title || id)
                .join(', ')}
              . ${SKYLITE_EXAM_PREP_CHART.bundle.yearUsd}/yr · ${SKYLITE_EXAM_PREP_CHART.bundle.rental48hUsd} / 48h
              weekend pack.
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              {SKYLITE_EXAM_PREP_CHART.bundle.note}
            </Typography>
          </Box>
        </Paper>

        <Box
          sx={{
            position: 'relative',
            borderRadius: 3,
            px: { xs: 1.5, sm: 3 },
            pt: 3,
            pb: 2.5,
            background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 70%, #020617 100%)',
            border: '1px solid rgba(167, 139, 250, 0.35)',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-end',
              gap: { xs: 1.25, sm: 2.5 },
              flexWrap: 'wrap',
              position: 'relative',
              zIndex: 1,
              pb: 1
            }}
          >
            {SKYLITE_VINYL_BOXES.map((box) => (
              <VinylCard
                key={box.id}
                box={box}
                flipped={flippedId === box.id}
                onFlip={handleFlip}
              />
            ))}
          </Box>
          <Box
            sx={{
              mt: 0.5,
              height: 14,
              borderRadius: '2px',
              background: 'linear-gradient(180deg, #a16207 0%, #78350f 40%, #451a03 100%)',
              boxShadow: '0 8px 18px rgba(0,0,0,0.45)',
              borderTop: '1px solid rgba(253, 230, 138, 0.35)'
            }}
          />
          <Box
            sx={{
              height: 10,
              mx: 1,
              background: 'linear-gradient(180deg, #451a03 0%, #1c1917 100%)',
              borderRadius: '0 0 4px 4px',
              opacity: 0.9
            }}
          />
        </Box>

        <SkyliteDetailPanel
          box={detailBox}
          panelRef={detailPanelRef}
          onClose={() => setDetailId(null)}
        />
      </Box>
    </Box>
  );
}
